// auth.js

import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

import useForcedLogoutSession from '@tt/hooks';

import { useUserState } from '../../store';
import config from '../config';
import { removeCookie } from '../cookie';
import { useMockCookie } from '../localAuth';
import phantom from '../phantom';

const useAuthentication = () => {
    const { getUser, refreshUser, resetSiteConfigs, resetUser, hasJournalAccess } = useUserState();
    const { pathname, search } = window.location;
    const isJournalingSSO = pathname.startsWith('/');
    const [isUserSet, setIsUserSet] = useState(false);
    const userToken = getUser().token;
    const tradingTerminalBaseUrl =
        config.get('TRADING_TERMINAL_BASE_URL') || 'https://tradingterminal.com';

    // Public journal IDs can be TRPCT/TRPTT/TTD/TTF and may include alphanumeric tails.
    const isPublicViewUrl = /^\/(TRPCT|TRPTT|TTD|TT|TTF)[A-Z0-9]+(\/.*)?$/i.test(pathname);
    const isPublicLeaderboardUrl = /^\/leaderboard(\/.*)?$/i.test(pathname);
    const hasValidSession = useCallback(
        (sessionData) =>
            Boolean(sessionData?.success === true && sessionData?.user && sessionData?.token),
        []
    );
    const redirectToGoPro = useCallback(() => {
        window.location.replace(`${tradingTerminalBaseUrl}/gopro`);
    }, [tradingTerminalBaseUrl]);
    const resetAndRedirectToGoPro = useCallback(async () => {
        await resetSiteConfigs();
        resetUser();
        setIsUserSet(false);
        redirectToGoPro();
    }, [redirectToGoPro, resetSiteConfigs, resetUser]);
    const handleForcedLogoutCleanup = useCallback(() => {
        resetSiteConfigs();
        resetUser();
        setIsUserSet(false);
    }, [resetSiteConfigs, resetUser]);

    // Session-status must hit Phantom (single-session / forced-logout). Use phantom client
    // so the request always goes to Phantom, not the journal backend.
    useForcedLogoutSession({
        enabled: !isPublicViewUrl && !isPublicLeaderboardUrl,
        token: userToken,
        client: phantom,
        onForcedLogout: handleForcedLogoutCleanup,
        onCleanup: () => removeCookie('user'),
        loginUrl: `${tradingTerminalBaseUrl}/auth/login`,
        pollingEndpoint: '/auth/session-status',
        pollingIntervalMs: 5000,
        skipOnAuthRoutes: true
    });

    useEffect(() => {
        // Skip if already authenticated
        if (isUserSet) return;

        const authenticateUser = async () => {
            // If we're in public view URL, bypass authentication
            if (isPublicViewUrl || isPublicLeaderboardUrl) {
                setIsUserSet(true);
                return;
            }

            const params = new URLSearchParams(search);

            if (isJournalingSSO && params.get('token')) {
                try {
                    const response = await axios.get(
                        `${config.get('PHANTOM_BASE_URL')}/user/sso?token=${params.get('token')}`
                    );
                    const { user, token: ssoToken } = response.data || {};

                    if (!user || !ssoToken) {
                        await resetAndRedirectToGoPro();
                        return;
                    }

                    const sessionData = await refreshUser(ssoToken);

                    if (!hasJournalAccess(sessionData)) {
                        if (hasValidSession(sessionData)) {
                            redirectToGoPro();
                            return;
                        }

                        await resetAndRedirectToGoPro();
                        return;
                    }

                    setIsUserSet(true);
                    resetSiteConfigs();
                } catch (error) {
                    console.error(error);
                    await resetAndRedirectToGoPro();
                }
            } else {
                const currentToken = getUser().token;
                const isAuthenticated = Boolean(currentToken);

                if (!isAuthenticated) {
                    window.location.replace(`${config.get('TRADING_TERMINAL_BASE_URL')}/journal`);
                    return;
                }

                // LOCAL MOCK AUTH — skip validateToken when useMockCookie is on.
                if (useMockCookie) {
                    setIsUserSet(true);
                    resetSiteConfigs();
                    return;
                }

                try {
                    const sessionData = await refreshUser(currentToken);

                    if (!hasJournalAccess(sessionData)) {
                        if (hasValidSession(sessionData)) {
                            redirectToGoPro();
                            return;
                        }

                        await resetAndRedirectToGoPro();
                        return;
                    }
                } catch (error) {
                    console.error(error);
                    await resetAndRedirectToGoPro();
                    return;
                }

                setIsUserSet(true);
                resetSiteConfigs();
            }
        };

        authenticateUser();
    }, [
        getUser,
        hasValidSession,
        hasJournalAccess,
        isJournalingSSO,
        isPublicLeaderboardUrl,
        isPublicViewUrl,
        isUserSet,
        refreshUser,
        redirectToGoPro,
        resetAndRedirectToGoPro,
        resetSiteConfigs,
        search
    ]);

    return isUserSet;
};

export default useAuthentication;
